import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Instagram, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, ParallaxImage } from "@/components/Motion";
import { useCategories } from "@/context/CategoriesContext";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { fmtHeight, fmtMeasure } from "@/lib/units";

const BK = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u && u.startsWith("/api/") ? `${BK}${u}` : u);

function Stat({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-baseline py-4 border-b border-white/10">
      <span className="overline-label !text-[10px] !font-semibold !text-white/85">{label}</span>
      <span className="font-display text-xl">{value}</span>
    </div>
  );
}

export function PortfolioView({ model, showBack = true }) {
  const { colors: categoryColors } = useCategories();

  const shareProfile = async () => {
    const shareUrl = `${window.location.origin}/api/share/models/${model.slug || model.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — it unfurls with the photo on WhatsApp, iMessage and socials.");
    } catch {
      window.prompt("Copy the share link:", shareUrl);
    }
  };

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="model-detail-page">
      {showBack && (
        <Link to="/models" data-testid="back-to-models" className="overline-label inline-flex items-center gap-2 hover:text-foreground transition-colors mb-12">
          <ArrowLeft size={14} /> Back to the talent board
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
        <div className="lg:col-span-6">
          <Reveal>
            <ParallaxImage src={src(model.photo_url)} alt={model.name} className="aspect-[3/4]" speed={8} />
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <p className="overline-label mb-4">{model.city}</p>
            <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-4" data-testid="model-name">
              {model.name}
            </h1>
            <div className="flex flex-wrap gap-2 mb-10">
              {(model.categories || []).map((c) => (
                <span
                  key={c}
                  style={{ borderColor: categoryColors[c] || "#888", color: categoryColors[c] || "#aaa" }}
                  className="font-grotesk font-medium text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5"
                >
                  {c}
                </span>
              ))}
            </div>
            {model.bio && (
              <div className="space-y-6 mb-12" data-testid="model-bio">
                {model.bio.split(/\n\s*\n+/).map((para, i) => (
                  <p key={i} className="text-white/85 leading-[1.8]">{para.replace(/\s*\n\s*/g, " ")}</p>
                ))}
              </div>
            )}
            <div data-testid="model-stats">
              <Stat label="Height" value={fmtHeight(model.height_cm)} />
              <Stat label="Bust / Chest" value={fmtMeasure(model.bust)} />
              <Stat label="Waist" value={fmtMeasure(model.waist)} />
              <Stat label="Hips" value={fmtMeasure(model.hips)} />
              <Stat label="Shoe" value={model.shoe} />
              <Stat label="Hair" value={model.hair} />
              <Stat label="Eyes" value={model.eyes} />
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={`/models/${model.slug || model.id}/card`} data-testid="view-comp-card" className="btn-primary">
                View comp card
              </Link>
              <button
                data-testid="share-profile-button"
                onClick={shareProfile}
                className="btn-outline inline-flex items-center gap-2"
              >
                <Share2 size={16} /> Share profile
              </button>
              {model.instagram && (
                <a
                  href={`https://instagram.com/${model.instagram.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="model-instagram-link"
                  className="btn-outline"
                >
                  <Instagram size={16} /> {model.instagram}
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </div>

      {(model.work_history || []).length > 0 && (
        <section className="mt-32" data-testid="work-history">
          <Reveal>
            <p className="overline-label mb-4">Selected Work</p>
            <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight mb-16">
              The <span className="italic text-accent">Credits</span>
            </h2>
          </Reveal>
          <div className="border-t border-white/10 max-w-4xl">
            {model.work_history.map((w, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 py-6 border-b border-white/10 items-baseline" data-testid={`credit-${i}`}>
                <span className="col-span-1 font-display italic text-xl text-white/25">{String(i + 1).padStart(2, "0")}</span>
                <span className="col-span-6 sm:col-span-5 font-display text-2xl">{w.client}</span>
                <span className="col-span-6 sm:col-span-4 text-sm text-muted-foreground">{w.role}</span>
                <span className="col-span-12 sm:col-span-2 sm:text-right overline-label !text-[10px]">{w.year}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {(model.reels || []).length > 0 && (
        <section className="mt-32" data-testid="demo-reels">
          <Reveal>
            <p className="overline-label mb-4">In Motion</p>
            <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight mb-16">
              Demo <span className="italic text-accent">Reels</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-5xl">
            {model.reels.map((r, i) => (
              <div key={r} className="bg-black" data-testid={`reel-player-${i}`}>
                <video src={src(r)} poster={(model.reel_thumbs || [])[i] ? src(model.reel_thumbs[i]) : undefined} controls preload="metadata" className="w-full aspect-video object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {(model.photos || []).length > 0 && (
        <section className="mt-32" data-testid="model-gallery">
          <Reveal>
            <p className="overline-label mb-4">The Book</p>
            <h2 className="font-display font-light text-4xl sm:text-6xl tracking-tight mb-16">
              Full <span className="italic text-accent">Range</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {model.photos.map((p, i) => (
              <Reveal key={p} delay={(i % 3) * 0.08}>
                <div className="aspect-[3/4] overflow-hidden bg-background" data-testid={`gallery-photo-${i}`}>
                  <img
                    src={src(p)}
                    alt={`${model.name} — frame ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 hover:scale-105 transition-all duration-700"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function ModelDetail() {
  const { id } = useParams();
  const [model, setModel] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/models/${id}`).then((r) => setModel(r.data)).catch(() => setError(true));
  }, [id]);

  /* Per-page SEO + Person structured data (runs once the profile loads) */
  usePageMeta(model ? {
    title: `${model.name} — ${model.city ? `Fashion Model in ${model.city}` : "Fashion Model"} | Solis Casting`,
    description:
      [model.city, (model.categories || []).join(", "), model.height_cm ? fmtHeight(model.height_cm) : null]
        .filter(Boolean).join(" · ") || "Fashion model on Solis Casting",
    image: model.photo_url ? src(model.photo_url) : undefined,
    url: `${window.location.origin}/models/${model.slug || model.id}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: model.name,
      jobTitle: "Fashion Model",
      url: `${window.location.origin}/models/${model.slug || model.id}`,
      ...(model.photo_url ? { image: src(model.photo_url) } : {}),
      ...(model.city ? { address: { "@type": "PostalAddress", addressLocality: model.city } } : {}),
      ...(model.gender ? { gender: model.gender } : {}),
      ...(model.height_cm ? { height: fmtHeight(model.height_cm) } : {}),
    },
  } : {});

  if (error)
    return (
      <div className="px-6 sm:px-10 py-32" data-testid="model-not-found">
        <p className="font-display italic text-4xl text-white/40">This face has left the room.</p>
      </div>
    );
  if (!model) return <div className="px-6 sm:px-10 py-32 overline-label">Loading…</div>;

  return <PortfolioView model={model} />;
}
